import { useIsMobile } from '@uplift/hooks';
import { DisplayName, getUserFullName } from '@uplift/utils';
import React, { useMemo } from 'react';
import { ModalFullPage } from '../../../ModalFullPage';
import { SessionDetailsGeneral } from './components/SessionDetailsGeneral';
import { SessionDetailsNotes } from './components/SessionDetailsNotes';
import { SessionDetailsAppointmentArg, useSessionDetailsModalData } from './SessionDetailsModal.hook';
import { UserStatus } from '@uplift/types';


export type SessionDetailsModalProps = {
  open: boolean;
  onClose: () => void;
  onCancelSession?: (appointmentId: string, patientName: DisplayName, status: UserStatus) => void;
  keepMounted?: boolean;
  appointment: SessionDetailsAppointmentArg;
};


export const SessionDetailsModal: React.FC<SessionDetailsModalProps> = (props) => {
  const [ page, setPage ] = React.useState<'general' | 'notes'>('general');
  const { data, isLoading } = useSessionDetailsModalData(props.appointment);
  const isMobile = useIsMobile();

  const handleClose = () => {
    props.onClose();
    setPage('general');
  };

  const patientDisplayName = useMemo(() => {
    if (isLoading || !data) {
      return { first: '', last: '' };
    }

    const { patientDetails } = data;

    if (!patientDetails) {
      return { first: '', last: '' };
    }

    return getUserFullName(patientDetails.firstName, patientDetails.lastName, patientDetails.preferredName);
  }, [ data, isLoading ]);

  return (
    <>
      <ModalFullPage
        data-appointment-id={props.appointment.id}
        open={props.open}
        onClose={handleClose}
        mobileOnly="md"
        maxWidth="sm"
        transition={isMobile}
        keepMounted={props.keepMounted}
      >
        <SessionDetailsGeneral
          loading={isLoading}
          shown={props.open}
          onClose={handleClose}
          onReviewNotes={() => setPage('notes')}
          onCancelSession={appointment => {
            props.onCancelSession?.(appointment.id, patientDisplayName, data?.patientDetails?.status as UserStatus ?? UserStatus.initial);
          }}
          appointment={data?.appointment}
          previousAppointment={data?.previousAppointment}
          patientDetails={data?.patientDetails}
        />
      </ModalFullPage>
      <ModalFullPage
        open={page === 'notes'}
        onClose={handleClose}
        mobileOnly="md"
        maxWidth="sm"
        transition={isMobile}
        keepMounted={props.keepMounted}
        slotProps={{
          backdrop: { invisible: true }
        }}
      >
        <SessionDetailsNotes
          open={page === 'notes'}
          onBack={() => setPage('general')}
          appointmentId={data?.previousAppointment?.id}
          appointmentDateTime={data?.previousAppointment?.fromDatetime}
        />
      </ModalFullPage>
    </>
  );
};
